import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import type {GenderType, ICustomer, IServiceTarget} from "@mytour/shared";
import {TARGET_TYPE_LABELS, TargetType} from "@mytour/shared";
import {Field2} from "@mytour/ui/form";
import {Splitter, SplitterButtons} from "@mytour/ui/layout";
import type React from "react";
import type {UseFormReturn} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {Button, Form} from "reactstrap";
import {z} from "zod";

export const serviceTargetSchema = z.object({
  label: z.string().optional(),
  type: z.nativeEnum(TargetType).optional(),
  gender: z.enum(["male", "gelding", "female"]).optional(),
  race: z.string().optional(),
  robe: z.string().optional(),
  height: z.number().optional().nullable(),
  weight: z.number().optional().nullable(),
  birthDate: z.string().optional(),
});

export type ServiceTargetForm = z.infer<typeof serviceTargetSchema>;

type Props = {
  serviceTarget: IServiceTarget;
  customer: ICustomer | null;
  hookForm: UseFormReturn<ServiceTargetForm>;
  pending: boolean;
  saveMode?: boolean;
  /** Restrict which target types are shown. When omitted, all types are shown. */
  allowedTargetTypes?: TargetType[];
  isEquine?: boolean;
  onClose: () => void;
  onReport: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
};

const ALL_TARGET_TYPES = [TargetType.HORSE, TargetType.DOG, TargetType.CAT, TargetType.HUMAN];

export function ServiceTargetForm({
  serviceTarget,
  customer,
  hookForm,
  pending,
  saveMode = false,
  allowedTargetTypes,
  isEquine = false,
  onClose,
  onReport,
  onSubmit,
}: Props) {
  const {t: translate} = useTranslation();
  const targetTypes = allowedTargetTypes ?? ALL_TARGET_TYPES;

  const isDirty = hookForm.formState.isDirty;

  const label = hookForm.watch("label");
  const type = hookForm.watch("type");
  const gender = hookForm.watch("gender");
  const height = hookForm.watch("height");
  const weight = hookForm.watch("weight");
  const race = hookForm.watch("race");
  const robe = hookForm.watch("robe");
  const birthDate = hookForm.watch("birthDate");

  const amendedServiceTarget = {...serviceTarget};
  amendedServiceTarget.label = label;
  amendedServiceTarget.type = type as TargetType;
  amendedServiceTarget.gender = gender as GenderType;
  amendedServiceTarget.height = height ?? undefined;
  amendedServiceTarget.weight = weight ?? undefined;
  amendedServiceTarget.race = race;
  amendedServiceTarget.robe = robe;
  amendedServiceTarget.birthDate = birthDate;

  return (
    <Form onSubmit={onSubmit}>
      <Field2 name="label" label="Nom" hookForm={hookForm} disabled={pending} />
      <Splitter>
        {!isEquine && (
          <Field2 name="type" label="Type" type="select" hookForm={hookForm} disabled={pending}>
            <option value="">Sélectionner</option>
            {targetTypes.map((tt) => (
              <option key={tt} value={tt}>
                {TARGET_TYPE_LABELS[tt]}
              </option>
            ))}
          </Field2>
        )}
        <Field2 name="gender" label="Sexe" type="select" hookForm={hookForm} disabled={pending}>
          <option value="">Sélectionner</option>
          {type === "horse" ? (
            <>
              <option value="female">Jument</option>
              <option value="gelding">Hongre</option>
              <option value="male">Entier</option>
            </>
          ) : type === "human" ? (
            <>
              <option value="female">Féminin</option>
              <option value="male">Masculin</option>
            </>
          ) : (
            <>
              <option value="female">Femelle</option>
              <option value="male">Mâle</option>
            </>
          )}
        </Field2>
      </Splitter>
      {type !== "human" && (
        <Splitter>
          <Field2 name="race" hookForm={hookForm} disabled={pending} />
          <Field2 name="robe" hookForm={hookForm} disabled={pending} />
        </Splitter>
      )}
      <Splitter>
        <Field2
          name="height"
          label="Taille (cm)"
          type="number"
          min={1}
          hookForm={hookForm}
          disabled={pending}
        />
        <Field2
          name="weight"
          label="Poids (kg)"
          type="number"
          min={1}
          hookForm={hookForm}
          disabled={pending}
        />
      </Splitter>

      <Field2
        name="birthDate"
        label="Date de naissance"
        type="date"
        hookForm={hookForm}
        disabled={pending}
      />
      {customer && serviceTarget.id && amendedServiceTarget.type === TargetType.HORSE && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "16px",
            marginBottom: "24px",
          }}
        >
          <Button color="primary" onClick={onReport} disabled={pending || isDirty} type="button">
            Comptes rendus
          </Button>
        </div>
      )}

      <SplitterButtons>
        <Button color="secondary" onClick={onClose} disabled={pending} type="button">
          {translate("entity.action.cancel")}
        </Button>
        <Button color="success" type="submit" disabled={pending}>
          {saveMode ? (
            <>
              <FontAwesomeIcon icon="save" />
              &nbsp; Sauvegarder
            </>
          ) : (
            "Valider"
          )}
        </Button>
      </SplitterButtons>
    </Form>
  );
}
